import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Modal,
    TextField,
    Typography,
    IconButton,
    List,
    ListItem,
    Switch,
    Pagination,
    AppBar, Toolbar, FormControlLabel, Checkbox, Avatar, CircularProgress
} from '@mui/material';
import {
    Edit,
    Delete,
    Add,
    Block,
    Security,
    SecurityOutlined,
    SecurityRounded,
    SdTwoTone,
    SecurityUpdate,
    SecurityUpdateWarning,
    PermIdentityOutlined,
    PermMediaOutlined,
    BlindsClosedOutlined,
    ChecklistOutlined
} from '@mui/icons-material';
import {
    fetchCompanyMembers,
    addCompanyMember,
    updateCompanyMember,
    deleteCompanyMember,
    setUserPermissions,
    fetchUserPermissions,
    getUserProfile
} from '../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HeaderAvatar from "../components/HeaderAvatar";
import Icon from "../components/Icon";

interface TeamMember {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    image_url: string;
    is_active: boolean;
}
interface Permissions {
    can_read_projects: boolean;
    can_edit_projects: boolean;
    can_read_products: boolean;
    can_edit_products: boolean;
}
interface UserProfile {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    image_url: string;
}

const Team: React.FC = () => {
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [currentMember, setCurrentMember] = useState<TeamMember | null>(null);
    const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [totalElements, setTotalElements] = useState(0);
    const [profile, setProfile] = useState<UserProfile>({
        id: '',
        first_name: '',
        last_name: '',
        email: '',
        image_url: '',
    });
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
    const [currentMemberPermissions, setCurrentMemberPermissions] = useState<Permissions>({
        can_read_projects: false,
        can_edit_projects: false,
        can_read_products: false,
        can_edit_products: false,
    });

    useEffect(() => {
        const pageParam = searchParams.get('page');
        if (pageParam) {
            setPage(Number(pageParam));
        }
    }, [searchParams]);

    useEffect(() => {
        loadTeamMembers();
    }, [page]);
    useEffect(() => {
        fetchUserProfile();
    }, []);
    const fetchUserProfile = async () => {
        try {
            const data = await getUserProfile();
            setProfile(data);
        } catch (error) {
        }
    };
    const loadTeamMembers = async () => {
        setLoading(true)
        try {
            const data = await fetchCompanyMembers(page, 10);
            setTeamMembers(data.users);
            setTotalPages(data.total_pages);
            setTotalElements(data.total);
            setLoading(false)
        } catch (error) {
            console.error('Error fetching team members:', error);
            setError('Failed to load team members');
            setLoading(false)
        }
    };

    const handleInvite = async () => {
        try {
            await addCompanyMember(inviteEmail);
            setIsInviteModalOpen(false);
            setSuccess('Invitation sent successfully');
            setInviteEmail('');
            loadTeamMembers();
        } catch (error) {
            console.error('Error inviting team member:', error);
            setError('Failed to send invitation');
        }
    };

    const handleUpdate = async () => {
        if (currentMember) {
            try {
                await updateCompanyMember(currentMember.id, { is_active: currentMember.is_active });
                setIsModalOpen(false);
                loadTeamMembers();
            } catch (error) {
                console.error('Error updating team member:', error);
                setError('Failed to update team member');
            }
        }
    };

    const handleDelete = async () => {
        if (memberToDelete) {
            try {
                await deleteCompanyMember(memberToDelete.id);
                setIsDeleteModalOpen(false);
                loadTeamMembers();
            } catch (error) {
                console.error('Error deleting team member:', error);
                setError('Failed to delete team member');
            }
        }
    };

    const openModal = (member: TeamMember) => {
        setCurrentMember(member);
        setIsModalOpen(true);
    };

    const openDeleteModal = (member: TeamMember) => {
        setMemberToDelete(member);
        setIsDeleteModalOpen(true);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
        setSearchParams({ page: newPage.toString() });
    };

    const openPermissionsModal = (member: TeamMember) => {
        setCurrentMember(member);
        fetchUserPermissions(member.id).then(setCurrentMemberPermissions);
        setIsPermissionsModalOpen(true);
    };

    const handlePermissionChange = (permission: keyof Permissions) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentMemberPermissions(prev => ({ ...prev, [permission]: event.target.checked }));
    };

    const handleSavePermissions = async () => {
        if (currentMember) {
            try {
                await setUserPermissions(currentMember.id, currentMemberPermissions);
                setIsPermissionsModalOpen(false);
                // Обновить список пользователей или текущего пользователя, если необходимо
            } catch (error) {
                console.error('Error setting user permissions:', error);
                setError('Failed to set user permissions');
            }
        }
    };
    const handleToggleActive = async (member: TeamMember) => {
        try {
            const updatedMember = { ...member, is_active: !member.is_active };
            await updateCompanyMember(member.id, { is_active: updatedMember.is_active });
            setTeamMembers(teamMembers.map(m => m.id === member.id ? updatedMember : m));
        } catch (error) {
            console.error('Error updating team member status:', error);
            setError('Failed to update team member status');
        }
    };
    const validateEmail = (email: any) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };
    return (
        <Box>
            <AppBar className={loading ? 'headerBlock preloading' : 'headerBlock'} position="static">
                <Toolbar className='toolbar'>
                    <Box className='toolbarTitle'>Команда <span>{totalElements}</span></Box>
                    <Button className="addToobarBtn" variant="contained" onClick={() => setIsInviteModalOpen(true)}><Add /></Button>
                    <HeaderAvatar/>
                </Toolbar>
            </AppBar>
            <Box className='topButtonsContainer hideMPageButtons'>
                <Box></Box>
                <Button className='topButtonsAdd' variant="outlined" onClick={() => setIsInviteModalOpen(true)}><Add /> Пригласть</Button>
            </Box>

            {!loading && (

                <Box className='teamList'>
                    {teamMembers.map((member) => (
                        <Box className='teamItem' key={member.id} sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                            <Avatar className='teamItemImage' src={member.image_url} sx={{ width: 54, height: 54 }}/>
                            <Box sx={{ flexGrow: 1 }} className='teamItemTitle'>
                                <Typography sx={{ flexGrow: 1, opacity: 0.8, color: '#1876d2' }}>{member.first_name} {member.last_name}</Typography>
                                <Typography sx={{ flexGrow: 1 }}>{member.email}</Typography>
                            </Box>
                            <Box>
                                { profile.id != member.id ? (
                                    <IconButton title='Права доступа' onClick={() => openPermissionsModal(member)}><Icon name="secure" /></IconButton>
                                ):null}
                                { profile.id != member.id ? (
                                    <Switch
                                        title='Активность'
                                        checked={member.is_active}
                                        onChange={() => handleToggleActive(member)}
                                        color="secondary"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#3a485b',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#3a485b',
                                            },
                                        }}
                                    />
                                ):null}
                                { profile.id != member.id ? (
                                        <IconButton onClick={() => openDeleteModal(member)}><Icon name="remove" /></IconButton>
                                    ):
                                    <Typography sx={{mr:1.4, opacity:0.4}}>это ваш аккаут</Typography>
                                }
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {(totalElements === 0 && !loading) && (
                <Box className='notFoundElements'>
                    <Typography>Пользователи не найдены</Typography>
                </Box>
            )}
            {loading && (
                <Box className='notFoundElements'>
                    <CircularProgress/>
                </Box>
            )}

            {(totalPages > 1 && !loading) && (
                <Pagination className='paginationElement' count={totalPages} page={page} onChange={handlePageChange} />
            )}

            {/* Модальное окно приглашения */}
            <Modal open={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)}>
                <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                    <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 600 }}>
                        <Typography variant="h6">Пригласить участника</Typography>
                        <TextField
                            label="Email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            fullWidth
                            type="email"
                            margin="normal"
                        />
                        <Box sx={{display:'flex', justifyContent: 'space-between', mt: 2, gap: 2}}>
                            <Button className="blueModalButton" variant="contained" onClick={handleInvite}
                                    disabled={(!validateEmail(inviteEmail)) && true}
                            >
                                Отправить приглашение
                            </Button>
                            <Button className="blueModalButton" variant="outlined" onClick={()=>setIsInviteModalOpen(false)} style={{color: 'red', borderColor: 'red'}}>
                                Отмена
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Модальное окно удаления */}
            <Modal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <Box sx={{minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                    <Box sx={{ p: 4, bgcolor: 'white', margin: 'auto', width: 400,borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Вы действительно хотите удалить пользователя?
                        </Typography>
                        <Button variant="contained" color="secondary" onClick={handleDelete}>
                            Удалить
                        </Button>
                        <Button onClick={() => setIsDeleteModalOpen(false)} sx={{ ml: 2 }}>
                            Отмена
                        </Button>
                    </Box>
                </Box>
            </Modal>


            <Modal open={isPermissionsModalOpen} onClose={() => setIsPermissionsModalOpen(false)}>
                <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                    <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 700 }}>
                        {currentMember && (
                            <>
                                <Typography variant="h6" sx={{mb: 2}}>Редактировать права пользователя <span style={{color:"#1876d2"}}>{currentMember.email}</span></Typography>
                                <FormControlLabel className='premissionCheck'
                                                  control={<Checkbox checked={currentMemberPermissions.can_read_projects} onChange={handlePermissionChange('can_read_projects')} />}
                                                  label="Чтение проектов"
                                />
                                <FormControlLabel className='premissionCheck'
                                                  control={<Checkbox checked={currentMemberPermissions.can_edit_projects} onChange={handlePermissionChange('can_edit_projects')} />}
                                                  label="Редактирование проектов"
                                />
                                <FormControlLabel className='premissionCheck'
                                                  control={<Checkbox checked={currentMemberPermissions.can_read_products} onChange={handlePermissionChange('can_read_products')} />}
                                                  label="Чтение продуктов"
                                />
                                <FormControlLabel className='premissionCheck'
                                                  control={<Checkbox checked={currentMemberPermissions.can_edit_products} onChange={handlePermissionChange('can_edit_products')} />}
                                                  label="Редактирование продуктов"
                                />
                                <Box sx={{display:'flex', justifyContent: 'space-between', mt: 2}}>
                                    <Button className="blueModalButton" variant="contained" onClick={handleSavePermissions}>
                                        Сохранить
                                    </Button>
                                    <Button className="blueModalButton" variant="outlined" onClick={()=>setIsPermissionsModalOpen(false)} style={{color: 'red', borderColor: 'red'}}>
                                        Отмена
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default Team;